import _ from 'lodash'
import type { Ref } from '@wix/thunderbolt-types'
import { createRef } from '@wix/materializer'
import type { GhostPropertiesItem } from '@wix/thunderbolt-becky-root'
import type { InnerElementsDataItem, LayoutDataItems, Component, BaseDataItem } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import {
	createRefInResolvedRefsNamespaces,
	getDataPathForInnerElement,
	getSplitCompIdsFromInflatedId,
	isGhostPropertyItem,
	isHiddenInAllVariants,
} from '../../../byRefUtils'
import { createByRefsisConsumptionCompNode } from '../../../compNodeUtils'
import { getTemplateFromInflatedId } from '../../../../utils'
import {
	BUILDER_COMP_TO_NICKNAME_MAP_ASPECT,
	LAYOUT_INFLATED_MAP,
	MOBILE_PROPERTY_SUFFIX,
	SITE_NAMESPACE,
	STRUCTURE_ASPECT,
} from '../../../constants'

export type isGhostCompToViewItem = {
	isGhostComp: boolean
	inflatedStructure?: Component
	isHiddenInAllVariants?: boolean
}

type MaybeGhostPropertyItem = GhostPropertiesItem | undefined
type IsGhostComponentDeps = {
	templateComp: Ref<Component>
	hostOverrideDesktopPropertyItem: Ref<MaybeGhostPropertyItem>
	hostOverrideMobilePropertyItem: Ref<MaybeGhostPropertyItem> | null
	propertyItem: Ref<MaybeGhostPropertyItem>
	layoutDataItems: Ref<Array<LayoutDataItems>> | null
	isMobileView: boolean
	refCompDataItem: Ref<BaseDataItem> | null
	refCompInnerElementsData: Ref<InnerElementsDataItem> | null
}

const {
	data: {
		DATA_MAPS: { PROPERTIES, DATA, INNER_ELEMENTS },
	},
	components: {
		PROPERTY_TYPES: { PROPERTY_QUERY },
	},
} = pointers

export const isGhostComponent = createByRefsisConsumptionCompNode<
	IsGhostComponentDeps,
	isGhostCompToViewItem | undefined
>({
	getDependencies: (
		templateCompId,
		{ id, layoutQuery },
		_ancestorRefCompId,
		scopedCreateRef,
		isMobileView,
		refCompData
	) => {
		const propertyQuery = `${id}-${PROPERTY_QUERY}`
		const mobilePropertyQuery = `${id}-${PROPERTY_QUERY}${MOBILE_PROPERTY_SUFFIX}`

		return {
			templateComp: scopedCreateRef<Component>(STRUCTURE_ASPECT, templateCompId),
			hostOverrideDesktopPropertyItem: createRef<MaybeGhostPropertyItem>([
				SITE_NAMESPACE,
				PROPERTIES,
				propertyQuery,
			]),
			hostOverrideMobilePropertyItem: isMobileView
				? createRef<MaybeGhostPropertyItem>([SITE_NAMESPACE, PROPERTIES, mobilePropertyQuery])
				: null,
			propertyItem: createRefInResolvedRefsNamespaces<MaybeGhostPropertyItem>(
				PROPERTIES,
				isMobileView ? mobilePropertyQuery : propertyQuery
			),
			layoutDataItems: layoutQuery
				? createRefInResolvedRefsNamespaces<Array<LayoutDataItems>>(LAYOUT_INFLATED_MAP, layoutQuery)
				: null,
			isMobileView,
			refCompDataItem: refCompData?.dataQuery ? createRef([SITE_NAMESPACE, DATA, refCompData.dataQuery]) : null,
			refCompInnerElementsData: refCompData?.innerElementsQuery
				? createRef([SITE_NAMESPACE, INNER_ELEMENTS, refCompData.innerElementsQuery])
				: null,
		}
	},
	toViewItem: (
		inflatedStructure,
		{
			templateComp,
			hostOverrideDesktopPropertyItem,
			hostOverrideMobilePropertyItem,
			propertyItem,
			layoutDataItems,
			isMobileView,
			refCompDataItem,
			refCompInnerElementsData,
		},
		additionalData
	) => {
		if (!templateComp) {
			return
		}

		const ancestorChain = getSplitCompIdsFromInflatedId(inflatedStructure.id)

		const compIdToNicknameMap =
			_.get(additionalData?.rawModel, [SITE_NAMESPACE, BUILDER_COMP_TO_NICKNAME_MAP_ASPECT, ancestorChain[0]]) ||
			{}

		const elementsRemovalStatePath = ['wix', 'elementsRemovalState', compIdToNicknameMap[templateComp.id]]

		const dataPathForInnerElement = getDataPathForInnerElement({
			ancestorChain: ancestorChain.slice(2, ancestorChain.length - 1), // since removal data is stored on the parent data, we need to go up until the parent id and not all the way up to the inner element itself
			compIdToNicknameMap,
			firstInnerWidgetId: ancestorChain[1], // this is the widget that we start the traversal from.
			extraPath: ['dataQuery', ...elementsRemovalStatePath],
		})

		const isGhostCompFromRefCompData =
			_.get(refCompDataItem, elementsRemovalStatePath) === 'REMOVED' ||
			_.get(refCompInnerElementsData, dataPathForInnerElement) === 'REMOVED'

		if (
			isGhostPropertyItem(hostOverrideDesktopPropertyItem) ||
			(isMobileView && hostOverrideMobilePropertyItem && isGhostPropertyItem(hostOverrideMobilePropertyItem)) ||
			isGhostPropertyItem(propertyItem) ||
			isGhostCompFromRefCompData
		) {
			return {
				// inflatedStructure is needed in onInvalidation to be added to byRefModel for byRef Api.
				// It's added at this stage since structure node is dependent on isGhostComp and once it's computed, its structure is already overwritten.
				// In addition, we override the id with the original compId because that's what byRef Api expects.
				isGhostComp: true,
				inflatedStructure: {
					...inflatedStructure,
					id: getTemplateFromInflatedId(inflatedStructure.id) || inflatedStructure.id,
				},
			}
		}

		if (layoutDataItems && isHiddenInAllVariants(layoutDataItems)) {
			return {
				isGhostComp: true,
				isHiddenInAllVariants: true,
			}
		}

		return { isGhostComp: false }
	},
})
